import JSZip from "jszip";
import type { PostEntry } from "./PostPlanBuilder";
import type { FileAnalysisResult } from "./FileAnalyser";
import { USECASE_LABELS } from "./usecaseConfig";

interface DownloadPostPlanParams {
  entries: PostEntry[];
  analysedFiles: FileAnalysisResult[];
}

function buildPostPlanFile(entries: PostEntry[]): string {
  return entries
    .map((entry, index) => {
      const usecaseLabel =
        entry.usecase
          ? USECASE_LABELS[entry.usecase] ?? entry.usecase
          : "-";

      return [
        `Post ${index + 1}`,
        `Datei: ${entry.fileName}`,
        `Usecase: ${usecaseLabel}`,
        `Posttyp: ${entry.postType ?? "-"}`,
        `Post-Potenzial: ${entry.postPotential ?? "-"}`,
        `Geplant: ${entry.scheduledDate.toLocaleString("de-DE")}`,
        "",
        "Text:",
        entry.text || "-",
        "",
        "----------------------------------------",
      ].join("\n");
    })
    .join("\n\n");
}

function buildAnalysisFile(
  analysedFiles: FileAnalysisResult[]
): string {
  return analysedFiles
    .map((analysis) => {
      return [
        `Datei: ${analysis.fileName}`,
        `Post-Potenzial: ${analysis.postPotential ?? "-"}`,
        `Empfohlene Posttypen: ${
          analysis.suggestedPostTypes.length > 0
            ? analysis.suggestedPostTypes.join(", ")
            : "-"
        }`,
        `Empfohlene Templates: ${
          analysis.suggestedImageTemplates.length > 0
            ? analysis.suggestedImageTemplates.join(", ")
            : "-"
        }`,
        `Verwendung: ${analysis.suggestedUsage ?? "-"}`,
        `Begründung: ${analysis.reasoning ?? "-"}`,
        "----------------------------------------",
      ].join("\n");
    })
    .join("\n\n");
}

export async function downloadPostPlan({
  entries,
  analysedFiles,
}: DownloadPostPlanParams) {
  const zip = new JSZip();

  const textFolder = zip.folder("Texte");
  const imageFolder = zip.folder("Bilder");

  const selectedEntries = entries.filter((entry) => entry.selected);

  for (const entry of selectedEntries) {
    const usecase = entry.usecase ?? "unknown";
    const safeBaseName = sanitizeFileName(entry.baseName);
    const safeUsecase = sanitizeFileName(usecase);

    const exportBaseName = `${safeBaseName}__${safeUsecase}`;

    if (entry.text && textFolder) {
      textFolder.file(
        `${exportBaseName}.txt`,
        entry.text
      );
    }

    if (entry.imageDataUrl && imageFolder) {
      const base64 = getBase64Content(entry.imageDataUrl);

      if (base64) {
        imageFolder.file(
          `${exportBaseName}.png`,
          base64,
          { base64: true }
        );
      }
    }
  }

  zip.file(
    "Postplan.txt",
    buildPostPlanFile(selectedEntries)
  );

  zip.file(
    "Analysedaten.txt",
    buildAnalysisFile(analysedFiles)
  );

  const blob = await zip.generateAsync({
    type: "blob",
  });

  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");

  link.href = url;
  link.download = "Postplan.zip";

  document.body.appendChild(link);
  link.click();
  link.remove();

  URL.revokeObjectURL(url);
}

function sanitizeFileName(value: string): string {
  return value
    .replace(/[<>:"/\\|?*]/g, "_")
    .replace(/\s+/g, "_");
}

function getBase64Content(dataUrl: string): string | null {
  const commaIndex = dataUrl.indexOf(",");

  if (commaIndex === -1) {
    return null;
  }

  return dataUrl.slice(commaIndex + 1);
}